/**
 * THE STANDARD STYLE AND ITS INDOOR CONFIG — one copy of the numbers, so the driver and
 * passenger portals cannot drift apart on them.
 *
 * ── ⚠️ WHY STANDARD AT ALL, WHEN THE PORTAL'S STYLE IS `light-v10` ────────────────
 * Airport interiors. Mapbox publishes an indoor tileset covering 200+ airports and the
 * Standard style already knows how to draw it — floor plans, indoor labels, the floor
 * selector, all of it. `showIndoor` is a CONFIGURATION PROPERTY OF STANDARD, not a layer:
 * classic styles like `light-v10` have no config surface at all, so there is nothing to
 * switch on there. Drawing the interior onto a classic style would mean reimplementing
 * room fills, floor filtering, indoor labels and floor switching by hand.
 *
 * ── ⚠️ THE CONFIG GOES ON THE CONSTRUCTOR, NEVER IN A `load` HANDLER ──────────────
 * Indoor features are extracted from vector tiles AT PARSE TIME. A map created with the
 * defaults parses the tiles around its opening camera with indoor off, and those tiles are
 * never re-parsed — so the interior stays missing until the user pans far enough to
 * request different tile ids, which is the "it only appears after you move it" bug the
 * mobile team spent days on. Passing `config` at instantiation means it is set before the
 * first tile request.
 *
 * ── ⚠️ A REJECTED PROPERTY IS SWALLOWED SILENTLY ──────────────────────────────────
 * No throw, no warning. If the interior does not appear, comment these out one at a time
 * rather than looking for an error that will never come.
 */

/** Mapbox GL JS v3+ only. The Standard style does not exist in v2. */
export const STANDARD_STYLE_URL = 'mapbox://styles/mapbox/standard'

/**
 * `basemap` is the IMPORT ID of Standard inside the style document, and it is what
 * `setConfigProperty` takes as its first argument. It is `basemap` for
 * `mapbox://styles/mapbox/standard`; a custom style importing Standard under another name
 * would use that name instead.
 *
 * ⚠️ VALUES ARE JSON BOOLEANS, NOT STRINGS. Mapbox does not coerce `'true'`.
 *
 * Copied from the mobile apps' `HopIndoorMapBox.tsx` so the three clients look like one
 * product. `densityPointOfInterestLabels` is not on the public Standard configuration
 * page but is in use and working on mobile; if it is rejected it is ignored rather than
 * throwing.
 */
export const INDOOR_STYLE_CONFIG = {
    basemap: {
        showIndoor: true,
        showIndoorLabels: true,
        showPointOfInterestLabels: true,
        showPlaceLabels: true,
        showTransitLabels: true,
        /* ⚠️ OFF, AND REPLACED — see `ROAD_LABEL_LAYER` below. Standard sets road names
           in UPPERCASE and no property changes that; `font` swaps the typeface, not the
           casing, and an imported style's layers cannot be edited. Drawing them ourselves
           from the same data is the only way to get the names in their real case.

           ⚠️ IT WAS BRIEFLY `true` (2026-09-23), when the three custom layers were removed
           on request. That broke things and was reverted the same day. The `color*`
           overrides above stay gone — a separate change, and not the one that broke. */
        showRoadLabels: false,
        showPedestrianRoads: true,
        /* ⚠️ OFF, THOUGH THE MOBILE APPS HAVE THEM ON. These draw Mapbox's hand-built
           landmark models, which exist only in the cities Mapbox has actually modelled.
           Everywhere else the browser asks for artwork that was never made and the tile
           server answers 404 — harmless, since a missing tile renders as an empty one, but
           it fills the console with red on every pan and buries whatever is being debugged.
           Turned off on request, 2026-09-11.

           ⚠️ THEY ARE NOT THE INDOOR LABELS. Gates, carousels and shops come from
           `showIndoorLabels` and `showPointOfInterestLabels` above and are unaffected. */
        showLandmarkIcons: false,
        showLandmarkIconLabels: false,
        densityPointOfInterestLabels: 5,
        /* ⚠️ NO COLOUR OVERRIDES HERE ANY MORE (2026-09-23). `colorRoads`,
           `colorMotorways`, `colorTrunks` (all white) and `colorGreenspace` (#e6f4e5) were
           the last of the old `light-v10` house palette painted over Standard, and they are
           gone on request: Standard draws in its own colours now.

           ⚠️ STANDARD IS A *LIT* STYLE, which is why repainting it piecemeal was never
           quite right. Its palette shifts with the dawn/day/dusk/night presets and its
           roads, land and labels are balanced against each other for each of them; a flat
           white pinned over two of those three survives the balance only at noon. Removing
           the overrides is what lets the basemap stay consistent with itself.

           Anything added back belongs here, on the config — not as a layer drawn over the
           top, which is what `BUILDING_LAYER` below still is. */
    },
}

/**
 * WHERE OUR OWN LAYERS GO IN A STANDARD MAP.
 *
 * Standard is an IMPORTED style, so its internal layers cannot be reordered and a layer
 * appended to the end is not simply "on top" in any way you control. Standard offers three
 * named insertion points instead — `bottom`, `middle`, `top`.
 *
 * `top` is what the mobile driver app uses for its route line and car, deliberately, so
 * they sit above the indoor floor plan rather than under it. The route is the instruction;
 * nothing on the basemap outranks it.
 */
export const ROUTE_SLOT = 'top'

/**
 * ⚠️ STANDARD IS A *LIT* STYLE, and this is the one that will waste an afternoon.
 *
 * It has a real lighting model with dawn/day/dusk/night presets, and custom layers are lit
 * along with everything else. A mint route line at `dusk` renders muddy brown and the
 * instinct is to go looking for a wrong colour. Emissive strength 1 makes a layer ignore
 * the lighting and draw at its literal colour, which is what a route line has to do — it
 * is signage, not scenery.
 *
 * Harmless on classic styles, which have no lighting model to ignore.
 */
export const EMISSIVE = 1

/**
 * ── ⚠️ WHAT USED TO BE HERE, AND WHY IT IS NOT ─────────────────────────
 *
 * A house palette (a monochrome theme, a pinned light preset and a dozen `color*`
 * properties), a replacement road-label layer in mixed case, and a building fill drawn
 * from Mapbox Streets — all of it an attempt to make Standard look like the `light-v10`
 * this screen used to render. Removed on request, 2026-09-10: Standard now draws itself.
 *
 * Three things worth knowing before any of it comes back:
 *
 *   · THE GROUND AND THE BUILDINGS ARE ONE DECISION. Roads are white and cannot go
 *     lighter, so what gives a street its edge is the block beside it. Setting one without
 *     the other flattens the map into a blank sheet.
 *   · THERE IS NO BUILDING COLOUR PROPERTY. Standard exposes only the highlight and
 *     select states, so recolouring buildings means drawing them yourself over the top.
 *   · ROAD LABELS ARE UPPERCASE AND NOT CONFIGURABLE. `font` changes the typeface, not
 *     the casing, and imported layers cannot be edited. Mixed case means switching
 *     Standard's road labels off and drawing your own from Mapbox Streets — which costs a
 *     second vector source.
 */

/**
 * OUR OWN ROAD LABELS, IN THE CASING THE DATA ACTUALLY HAS.
 *
 * ⚠️ IT IS NOT A TRANSFORM. `name` in Mapbox Streets is already the real name — "Shah
 * Jilani Road" — and Standard was uppercasing it at draw time. This simply does not.
 *
 * ⚠️ IT COSTS A SECOND VECTOR SOURCE. Mapbox Streets is not the tileset Standard reads,
 * so these labels are extra tile requests against the same account. That is the price of
 * the casing, and it is the reason this is the ONLY thing drawn from that source — the
 * building fill that once shared it was removed with the rest of the house palette.
 */
export const ROAD_LABEL_SOURCE_ID = 'hopn-streets'
export const ROAD_LABEL_LAYER_ID = 'hopn-road-labels'
export const ROAD_LABEL_SOURCE = {type: 'vector', url: 'mapbox://mapbox.mapbox-streets-v8'}

/**
 * ⚠️ `middle`, NOT `top`. Top is where the route line and the car go, and a road name
 * drawn over the route reads as part of it. Middle puts these above the basemap and below
 * the instruction, which is the order they should be read in.
 *
 * ⚠️ AND THEY STILL COLLIDE PROPERLY. Same renderer, same collision index — these are
 * laid out against Standard's remaining labels rather than on top of them, so switching
 * road names off and drawing our own does not double them up or crowd the place names.
 */
export const ROAD_LABEL_LAYER = {
    id: ROAD_LABEL_LAYER_ID,
    type: 'symbol',
    slot: 'middle',
    source: ROAD_LABEL_SOURCE_ID,
    'source-layer': 'road',
    /* The classes a driver navigates by. Service roads, tracks and paths are named in the
       data too, and drawing all of them buries the streets that matter under alley names. */
    filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['has', 'name'],
        ['match', ['get', 'class'],
            ['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'street'], true,
            false],
    ],
    minzoom: 12,
    layout: {
        'symbol-placement': 'line',
        /* `name_en` first so an Urdu or Arabic street reads in the app's language, falling
           back to the local name rather than to nothing. */
        'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
        'text-size': ['interpolate', ['linear'], ['zoom'], 12, 10, 18, 13],
        'text-max-angle': 30,
        'text-padding': 2,
    },
    paint: {
        'text-color': '#5a5a5a',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.25,
        // Standard is lit; without this the labels dim with the basemap. See `EMISSIVE`.
        'text-emissive-strength': EMISSIVE,
    },
}

export const ROAD_SNAP_LAYER_ID = 'hopn-road-snap'

export const ROAD_SNAP_LAYER = {
    id: ROAD_SNAP_LAYER_ID,
    type: 'line',
    slot: 'bottom',
    source: ROAD_LABEL_SOURCE_ID,
    'source-layer': 'road',
    /* ⚠️ NOT BELOW ~14. Mapbox Streets generalises geometry as it zooms out — at 12 a
       junction is a couple of vertices and snapping to it would place the car metres from
       the road it is drawn on. The driving camera sits at 17.5; this is only a floor. */
    minzoom: 14,
    filter: ['all',
        ['==', ['geometry-type'], 'LineString'],
        ['match', ['get', 'class'],
            ['motorway', 'motorway_link', 'trunk', 'trunk_link', 'primary', 'primary_link',
                'secondary', 'secondary_link', 'tertiary', 'tertiary_link',
                'street', 'street_limited', 'service', 'track'], true,
            false],
    ],
    paint: {'line-opacity': 0, 'line-width': 1},
}

/**
 * BUILDINGS, DRAWN OURSELVES — a grey block with a border, over Standard's own footprints.
 *
 * ⚠️ BECAUSE STANDARD EXPOSES NO BUILDING COLOUR. The only building properties in its
 * configuration surface are the HIGHLIGHT and SELECT states; the base fill belongs to the
 * style and cannot be reached, imported layers being uneditable. Drawing over the top is
 * the whole of the available technique.
 *
 * ⚠️ IT REUSES THE LABEL SOURCE, so it costs no additional tiles — Mapbox Streets is
 * already being fetched for the road names.
 *
 * ⚠️ `bottom`, AND IT WAS `middle` UNTIL IT ATE AN AIRPORT. Middle draws above the
 * basemap's fills, which includes Standard's indoor floor plans — and a terminal is ONE
 * enormous footprint in Mapbox Streets, so this layer painted a single grey block straight
 * over the interior. The indoor POI labels still came through, sitting on top of nothing,
 * which is what "indoor is not working" looked like from the outside.
 *
 * Bottom draws below roads and floor plans, so the interior wins where there is one. There
 * is nothing of Standard's left to be covered by anyway: `show3dObjects` is off in both
 * camera modes, which is what stops it drawing buildings of its own.
 *
 * ⚠️ 2D ONLY, in effect. `show3dObjects` follows the camera, so in 3D the extrusions
 * rise straight through this flat fill and hide it — which is the right outcome.
 */
export const BUILDING_LAYER_ID = 'hopn-buildings'

export const BUILDING_LAYER = {
    id: BUILDING_LAYER_ID,
    type: 'fill',
    slot: 'bottom',
    source: ROAD_LABEL_SOURCE_ID,
    'source-layer': 'building',
    minzoom: 14,
    paint: {
        // Specified by the account holder, 2026-09-10.
        'fill-color': '#e9e9e7',
        /* ⚠️ CLEARLY DARKER THAN THE FILL, NOT A SHADE OFF IT. An earlier pass set these
           within two points of each other and the border simply did not exist — which
           matters because the border is the only thing separating a terrace into houses.
           A row of buildings without it is one grey mass. */
        'fill-outline-color': '#d2d2cf',
    },
}

/**
 * THE SAME BUILDINGS, RAISED — what the flat fill above becomes when the camera tilts.
 *
 * ⚠️ STANDARD'S OWN 3D BUILDINGS ARE SWITCHED OFF TO MAKE ROOM FOR THIS, and that is
 * the trade. Their colour is not configurable either — same missing property as the flat
 * fill — so a tilted map drew Mapbox's grey next to our `#e9e9e7` and the two modes did
 * not match. The only way to hold one colour across both is to draw both ourselves.
 *
 * ⚠️ IT COSTS THE TREES AND THE LANDMARKS. `show3dObjects` is one switch over
 * buildings, trees and Mapbox's hand-built landmark models; there is no way to keep those
 * while dropping the buildings. If the landmarks turn out to matter more than the colour,
 * this layer is what to remove.
 *
 * ⚠️ NOT EMISSIVE, DELIBERATELY — unlike the route line. Standard's lighting is what
 * shades a wall differently from a roof, and that shading is the only thing making these
 * read as solid rather than as grey confetti. A route line is signage and must ignore the
 * light; a building is scenery and must obey it.
 */
export const BUILDING_3D_LAYER_ID = 'hopn-buildings-3d'

export const BUILDING_3D_LAYER = {
    id: BUILDING_3D_LAYER_ID,
    type: 'fill-extrusion',
    /* Same slot as the flat fill above, and for the same reason — a raised terminal would
       bury its own floor plan just as thoroughly as a flat one did. */
    slot: 'bottom',
    source: ROAD_LABEL_SOURCE_ID,
    'source-layer': 'building',
    minzoom: 14,
    /* `extrude` is a STRING in Mapbox Streets, not a boolean. Comparing it to `true`
       matches nothing and draws an empty layer. */
    filter: ['==', ['get', 'extrude'], 'true'],
    paint: {
        'fill-extrusion-color': '#e9e9e7',
        /* Grown over the first fifth of a zoom level rather than switched on at 14 —
           snapping a city into existence in one frame reads as a glitch. */
        'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 14, 0, 14.05, ['get', 'height']],
        'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], 14, 0, 14.05, ['get', 'min_height']],
        /* Not opaque. The route line is the instruction and it passes BEHIND buildings on
           a tilted map; at full opacity a block hides the next turn completely. */
        'fill-extrusion-opacity': 0.95,
    },
}
